/** SEO head and structured data for the comparisons hub (`/compare`). */

import { isProduction } from "@/config/config";
import { COMPARE_PAGE } from "@/content/marketing";
import { PUBLISHER_NODE, SITE_URL, WEBSITE_NODE } from "@/lib/marketing-jsonld";

export const COMPARE_URL = "https://goodsender.com/compare";

export const COMPARE_DOCUMENT_TITLE =
  "GoodSender compared: blog and newsletter platforms side by side";

export const COMPARE_DESCRIPTION = COMPARE_PAGE.lede;

const COMPARE_OG_IMAGE = "https://goodsender.com/og-image.png";

/** The articles the hub lists, as the structured data needs them. */
export type CompareHubEntry = { title: string; path: string };

/**
 * A CollectionPage whose `mainEntity` is the ordered list of comparisons, in
 * the same order the page shows them. Item URLs are absolute on goodsender.com
 * like every other marketing node here.
 */
export function compareJsonLd(articles: readonly CompareHubEntry[]) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${COMPARE_URL}#collection`,
    url: COMPARE_URL,
    name: COMPARE_DOCUMENT_TITLE,
    description: COMPARE_DESCRIPTION,
    inLanguage: "en",
    isPartOf: WEBSITE_NODE,
    publisher: PUBLISHER_NODE,
    mainEntity: {
      "@type": "ItemList",
      itemListOrder: "https://schema.org/ItemListOrderDescending",
      numberOfItems: articles.length,
      itemListElement: articles.map((article, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: article.title,
        url: `https://goodsender.com${article.path}`,
      })),
    },
  };
}

export const compareBreadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "Compare", item: COMPARE_URL },
  ],
};

/** Route-level `head()` for /compare, shaped like `faqRouteHead`. */
export function compareRouteHead() {
  return {
    meta: [
      { title: COMPARE_DOCUMENT_TITLE },
      { name: "description", content: COMPARE_DESCRIPTION },
      {
        name: "robots",
        content: isProduction
          ? "index, follow, max-image-preview:large"
          : "noindex, nofollow",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: COMPARE_URL },
      { property: "og:title", content: COMPARE_DOCUMENT_TITLE },
      { property: "og:description", content: COMPARE_DESCRIPTION },
      { property: "og:image", content: COMPARE_OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:site_name", content: "GoodSender" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: COMPARE_DOCUMENT_TITLE },
      { name: "twitter:description", content: COMPARE_DESCRIPTION },
      { name: "twitter:image", content: COMPARE_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: COMPARE_URL }],
  };
}
